import {
  Object3D,
  Plane,
  Color,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Vector3,
  PlaneGeometry,
  DoubleSide,
  Box3,
  Group,
  Quaternion,
} from "three";
import { TransformControls } from "three/examples/jsm/controls/TransformControls.js";
import { BaseManager } from "./BaseManager.js";

/**
 * 包含轴向剖切，自由剖切和盒子剖切  
 * 1、使用setLength()函数设置三个轴向的长度，（默认以传进来的物体的包围盒作为长度）
 * 2、使用setObjectToClipArray()函数将需要剖切的对象加入数组
 * 3、使用updateClipAxis()函数开始剖切或切换剖切类型
 * 4、自由剖切时可以调用changeTransformControlsMode()函数切换平移、旋转和缩放

 * 6、使用resetClipPos()函数来重置所有剖切板位置
 * 7、使用exitClip()函数退出剖切
 * 8、使用dispose()函数释放所有剖切板占用的资源
 */
class ClippingManager extends BaseManager {
  constructor(manager) {
    super(manager);
  }
  init(manager) {
    super.init(manager);

    this.sceneManager = manager.sceneManager;
    this.scene = this.sceneManager.scene;
    this.renderer = this.sceneManager.renderer;
    this.camera = this.sceneManager.activeCamera;
    this.renderer.localClippingEnabled = true; //允许剖切
    this.transformControls = this.sceneManager.transformcontrol;
    this.transformControls.addEventListener("change", (object) => {
      this.updateSelf();
    });

    this.transformcontrol_box = new TransformControls(
      this.camera,
      this.renderer.domElement
    );
    this.transformcontrol_box.addEventListener("dragging-changed", (event) => {
      if (this.sceneManager.controls)
        this.sceneManager.controls.enabled = !event.value;
      if (this.sceneManager.customControls)
        this.sceneManager.customControls.enabled = !event.value;
    });
    this.transformcontrol_box.addEventListener("change", (object) => {
      if (this.transformcontrol_box.object) {
        if (this.transformcontrol_box.object._axis == "Z") this.zChanged = true;
        else if (this.transformcontrol_box.object._axis == "X")
          this.xChanged = true;
        else if (this.transformcontrol_box.object._axis == "Y")
          this.yChanged = true;
        this.updateSelf();
      }
    });

    //剖切模式，全局剖切还是针对部分物体,如果使用"local"，则需要使用setObjectToClipArray()函数
    // 设置剖切物体   "global"/"local"     全局的有问题，transformControls 的轴也会被剖切
    this.clipMode = "global";

    this.isInit = false; //是否已经初始化剖切板

    this.clippingOpacity = 0.05; //剖切板透明度
    this.clippingColor = "#00ffff"; //剖切板颜色

    this.lengthX = 10;
    this.lengthY = 10;
    this.lengthZ = 10;
    this.isSetLength = false;

    this.allClippingObjects = []; //保存所有需要剖切的对象,方便重新计算包围盒大小

    this.center = new Vector3();

    this.initStat();

    this.objBox = null;
  }

  update(dt) {
    super.update(dt);
  }

  clear() {
    this.dispose();
    super.clear();
  }

  //---------------------------------------------------------------------------------

  initStat() {
    if (!this.clips) {
      this.clips = {
        axis: null,
        materials: [], //保存需要剖切的材质
        //X轴向剖切
        clipXPlane: null,
        clipXMesh: null,
        //Y轴向剖切
        clipYPlane: null,
        clipYMesh: null,
        //Z轴向剖切
        clipZPlane: null,
        clipZMesh: null,
        //自由剖切
        clipFreePlane: [],
        clipFreeMesh: [],
        //盒子剖切
        clipBoxPlanes: [],
        clipBoxMeshs: [],
      };
    } else {
      this.clips.axis = null;
      this.clips.clipXPlane = null;
      this.clips.clipXMesh = null;
      this.clips.clipYPlane = null;
      this.clips.clipYMesh = null;
      this.clips.clipZPlane = null;
      this.clips.clipZMesh = null;
      this.clips.clipFreePlane = [];
      this.clips.clipFreeMesh = [];
      this.clips.clipBoxPlanes = [];
      this.clips.clipBoxMeshs = [];
    }

    //保存盒子剖切6个面的位移
    this.boxClipAxisLastPos = {
      x1: 0,
      x2: 0,
      y1: 0,
      y2: 0,
      z1: 0,
      z2: 0,
    };
  }

  /**
   * 需要射线检测点击 this.clips.clipBoxMeshs
   * @param {*} obj
   * @returns
   */
  onMouseUp(obj) {
    if (this.clips.axis != "BOX") return;
    if (obj._axis) {
      this.transformcontrol_box.attach(obj);
      switch (obj._axis) {
        case "Z":
          this.transformcontrol_box.showX = true;
          this.transformcontrol_box.showY = false;
          this.transformcontrol_box.showZ = false;
          this.zChanged = true;
          break;
        case "Y":
          this.transformcontrol_box.showX = false;
          this.transformcontrol_box.showY = true;
          this.transformcontrol_box.showZ = false;
          this.yChanged = true;
          break;
        case "X":
          this.transformcontrol_box.showX = false;
          this.transformcontrol_box.showY = false;
          this.transformcontrol_box.showZ = true;
          this.xChanged = true;
          break;
      }
    }
  }

  /**
   * 将物体加入剖切数组  外部调用
   * @param {需要剖切的对象} obj
   */
  setObjectToClipArray(obj) {
    //if(!this.objBox) this.objBox = new Box3();
    //this.objBox.expandByObject(obj);

    this.allClippingObjects.push(obj);
    if (obj instanceof Mesh) {
      this.clips.materials.push(obj.material);
    } else if (obj instanceof Object3D) {
      if (obj.type == "Points") {
        this.clips.materials.push(obj.material);
      } else {
        obj.traverse((node) => {
          if (node.isMesh) {
            node.material.side = DoubleSide; //双面渲染，优化剖切后模型的显示效果
            this.clips.materials.push(node.material);

            //添加剖切边缘高亮  如果模型材质原先替换过，这个操作会覆盖
            //node.material.onBeforeCompile = this.shadetFunction;
          } else if (node.type == "Sprite") {
            this.clips.materials.push(node.material);
          }
        });
      }
    }
  }

  shadetFunction(shader) {
    shader.uniforms.outlineColor = { value: new Color(1, 0, 0) };
    shader.uniforms.outlineWidth = { value: 0.01 };
    shader.fragmentShader =
      "uniform vec3 outlineColor;\nuniform float outlineWidth;\n" +
      shader.fragmentShader.replace(
        "#include <clipping_planes_fragment>",
        `#if NUM_CLIPPING_PLANES > 0
                vec4 plane;
                
                #pragma unroll_loop_start
                bool num=false;
                for ( int i = 0; i < UNION_CLIPPING_PLANES; i ++ ) {
                    plane = clippingPlanes[ i ];
                    if ( dot( vClipPosition, plane.xyz ) >= (plane.w-outlineWidth)&& dot( vClipPosition, plane.xyz ) <= plane.w) 
                    {
                        num=true;
                    }
                    if ( dot( vClipPosition, plane.xyz ) > plane.w ) 
                        discard;
                }
                if(num){
                    gl_FragColor=vec4(outlineColor,1.);
                    return;
                }
                #pragma unroll_loop_end

                #if UNION_CLIPPING_PLANES < NUM_CLIPPING_PLANES
                bool clipped = true;
                #pragma unroll_loop_start
                for ( int i = UNION_CLIPPING_PLANES; i < NUM_CLIPPING_PLANES; i ++ ) {
                    plane = clippingPlanes[ i ];
                    clipped = ( dot( vClipPosition, plane.xyz ) > plane.w ) && clipped;
                }
                #pragma unroll_loop_end
                if ( clipped ) discard;
                #endif\n
                #endif`
      );

    this.userData.shader = shader;
  }

  /**
   * 设置各个剖切板的长宽高,不会动态更新，所以要先调用这个函数修改长宽高，再调用updateClipAxis函数显示和初始化剖切板 目前根据模型包围盒确定大小，不需要这个函数
   * 外部调用
   */
  setLength(x = 10, y = 10, z = 10) {
    this.lengthX = x;
    this.lengthY = y;
    this.lengthZ = z;
    this.isSetLength = true;
  }

  /**
   * 重置所有剖切板位置 ，外部调用
   */
  resetClipPos() {
    this.clips.clipXMesh.position.copy(this.clips.clipXMesh._srcPos);
    this.clips.clipYMesh.position.copy(this.clips.clipYMesh._srcPos);
    this.clips.clipZMesh.position.copy(this.clips.clipZMesh._srcPos);

    for (var i = 0; i < this.clips.clipFreeMesh.length; i++) {
      this.clips.clipFreeMesh[i].position.copy(
        this.clips.clipFreeMesh[i]._srcPos
      );
      this.clips.clipFreeMesh[i].quaternion.copy(
        this.clips.clipFreeMesh[i]._srcEuler
      );
    }

    for (var i = 0; i < this.clips.clipBoxMeshs.length; i++) {
      this.clips.clipBoxMeshs[i].position.copy(
        this.clips.clipBoxMeshs[i]._srcPos
      );
    }
    this.updateSelf();
  }

  /**
   * 切换或显示剖切板  外部调用
   * @param {0:轴向剖切 1:自由剖切 2:盒子剖切} mode
   * @param {轴向剖切时才有效  "+X"/"-X"/"+Y"/"-Y"/"+Z"/"-Z"} axis
   */
  changeClipAxis(mode = 0, axis = "+X") {
    if (this.clipMode !== "global" && this.clips.materials.length <= 0) {
      console.warn(
        "请先使用 setObjectToClipArray(obj) 函数将需要剖切的物体添加到数组中！"
      );
      return;
    }

    //切换到相同剖切时直接返回
    if (mode == 1 && this.clips.axis == "FREE") return;
    else if (mode == 2 && this.clips.axis == "BOX") return;

    //把原来的剖切关闭
    this.exitClip();

    //重新计算剖切版位置和大小
    if (mode == 0) this.initPlane(axis);
    else if (mode == 1) this.initPlane("FREE");
    else if (mode == 2) this.initPlane("BOX");

    if (mode == 0) {
      if (this.clips.axis == axis) return;
      switch (axis) {
        case "+X":
        case "-X":
          if (this.clips.clipXMesh) {
            this.clips.axis = "X";
            this.scene.add(this.clips.clipXMesh);
            // 当前项目不需要
            // if (this.transformControls) {
            //   this.transformControls.attach(this.clips.clipXMesh);
            //   this.transformControls.showX = true;
            //   this.transformControls.showY = false;
            //   this.transformControls.showZ = false;
            //   this.transformControls.space = "world";
            //   this.transformControls.mode = "translate";
            // }
            if (this.clipMode == "global") {
              this.renderer.clippingPlanes = [this.clips.clipXPlane];
            } else {
              for (var i = 0; i < this.clips.materials.length; i++) {
                this.clips.materials[i].clippingPlanes = [
                  this.clips.clipXPlane,
                ];
              }
            }
          }
          break;
        case "+Y":
        case "-Y":
          if (this.clips.clipYMesh) {
            this.clips.axis = "Y";
            this.scene.add(this.clips.clipYMesh);
            // 当前项目不需要
            // if (this.transformControls) {
            //   this.transformControls.attach(this.clips.clipYMesh);
            //   this.transformControls.showX = false;
            //   this.transformControls.showY = true;
            //   this.transformControls.showZ = false;
            //   this.transformControls.space = "world";
            //   this.transformControls.mode = "translate";
            // }
            if (this.clipMode == "global") {
              this.renderer.clippingPlanes = [this.clips.clipYPlane];
            } else {
              for (var i = 0; i < this.clips.materials.length; i++) {
                this.clips.materials[i].clippingPlanes = [
                  this.clips.clipYPlane,
                ];
              }
            }
          }
          break;
        case "+Z":
        case "-Z":
          if (this.clips.clipZMesh) {
            this.clips.axis = "Z";
            this.scene.add(this.clips.clipZMesh);
            // 当前项目不需要
            // if (this.transformControls) {
            //   this.transformControls.attach(this.clips.clipZMesh);
            //   this.transformControls.showX = false;
            //   this.transformControls.showY = false;
            //   this.transformControls.showZ = true;
            //   this.transformControls.space = "world";
            //   this.transformControls.mode = "translate";
            // }
            if (this.clipMode == "global") {
              this.renderer.clippingPlanes = [this.clips.clipZPlane];
            } else {
              for (var i = 0; i < this.clips.materials.length; i++) {
                this.clips.materials[i].clippingPlanes = [
                  this.clips.clipZPlane,
                ];
              }
            }
          }
          break;
      }
    } else if (mode == 1) {
      if (this.clips.clipFreeMesh.length > 0) {
        this.clips.axis = "FREE";
        for (var i = 0; i < this.clips.clipFreeMesh.length; i++) {
          this.scene.add(this.clips.clipFreeMesh[i]);
        }

        if (this.transformControls) {
          this.transformControls.attach(this.clips.clipFreeMesh[0]);
          this.transformControls.showX = true;
          this.transformControls.showY = true;
          this.transformControls.showZ = true;
          this.transformControls.space = "world";
          this.transformControls.mode = "translate";
        }
        if (this.clipMode == "global") {
          this.renderer.clippingPlanes = this.clips.clipFreePlane;
        } else {
          for (var i = 0; i < this.clips.materials.length; i++) {
            this.clips.materials[i].clippingPlanes = this.clips.clipFreePlane;
          }
        }
      }
    } else if (mode == 2) {
      this.clips.axis = "BOX";
      // for(var i=0;i<this.clips.clipBoxMeshs.length;i++){
      //     this.scene.add(this.clips.clipBoxMeshs[i]);
      // }
      this.scene.add(this.boxGroup);
      this.transformControls.setMode("translate");
      this.transformControls.showX = true;
      this.transformControls.showY = true;
      this.transformControls.showZ = true;
      this.transformControls.attach(this.boxGroup);

      this.scene.add(this.transformcontrol_box);
      this.transformcontrol_box.attach(this.clips.clipBoxMeshs[0]);
      this.transformcontrol_box.showZ = false;
      this.transformcontrol_box.showY = false;
      this.transformcontrol_box.showX = true;

      if (this.clipMode == "global") {
        this.renderer.clippingPlanes = this.clips.clipBoxPlanes;
      } else {
        for (var i = 0; i < this.clips.materials.length; i++) {
          this.clips.materials[i].clippingPlanes = this.clips.clipBoxPlanes;
        }
      }
    }
    this.updateSelf();
  }

  /**
   * 切换平移、旋转、缩放   外部调用
   * @param { "translate"/"rotate"/"scale"} mode
   */
  changeTransformControlsMode(mode = "translate") {
    if (this.transformControls && this.clips.axis == "FREE") {
      this.transformControls.setMode(mode);
    }
  }

  /**
   * 新增一个自由剖切板
   */
  addFreeClipPlane() {
    if (this.clips.axis == "FREE") {
      let newPlane = this.clips.clipFreePlane[0].clone();
      let newMesh = this.clips.clipFreeMesh[0].clone();
      newMesh._srcPos = this.clips.clipFreeMesh[0]._srcPos.clone();
      newMesh._srcEuler = this.clips.clipFreeMesh[0]._srcEuler.clone();
      newMesh._type = "clipping";
      newMesh.position.copy(newMesh._srcPos);
      newMesh.quaternion.copy(newMesh._srcEuler);
      newPlane.setFromNormalAndCoplanarPoint(
        newMesh.getWorldDirection(new Vector3()),
        newMesh.position
      );
      this.clips.clipFreeMesh.push(newMesh);
      this.clips.clipFreePlane.push(newPlane);
      this.scene.add(newMesh);
      if (this.transformControls) this.transformControls.attach(newMesh);
    }
  }

  /**
   * 根据索引移除一个自由剖切板
   */
  removeFreeClipPlaneByIndex(index) {
    if (
      index < 0 ||
      index >= this.clips.clipFreeMesh.length ||
      this.clips.clipFreeMesh.length == 1
    )
      return;
    if (this.clips.axis == "FREE") {
      let temp = this.clips.clipFreeMesh[index];
      this.clips.clipFreeMesh.splice(index, 1);
      this.clips.clipFreePlane.splice(index, 1);
      if (temp.parent) temp.parent.remove(temp);
      temp.material.dispose();
      temp.geometry.dispose();
      temp = null;
      this.transformControls.detach();
      if (this.clipMode == "global") {
        this.renderer.clippingPlanes = this.clips.clipFreePlane;
      } else {
        for (var i = 0; i < this.clips.materials.length; i++) {
          this.clips.materials[i].clippingPlanes = this.clips.clipFreePlane;
        }
      }
    }
  }

  /**
   * 根据索引选择自由剖切板
   * @param {} index
   */
  selectFreeClipPlaneByIndex(index) {
    if (
      index < 0 ||
      index >= this.clips.clipFreeMesh.length ||
      !this.transformControls
    )
      return;
    if (this.clips.axis == "FREE") {
      this.transformControls.attach(this.clips.clipFreeMesh[index]);
    }
  }

  /**
   * 修改盒子剖切的X轴方向的位移（负轴）  外部调用  弃用
   * @param {0到1的值} value
   */
  changeBoxValueXNegative(value) {
    if (this.clips.axis != "BOX") return;
    value = Math.round(value * 10000) / 10000; //为了避免传值过程中的微小误差进行这步操作
    if (value < 0 || value > 1 || this.boxClipAxisLastPos.x1 == value) return;
    if (value > this.boxClipAxisLastPos.x1) {
      let dis = (value - this.boxClipAxisLastPos.x1) * this.lengthZ;
      this.clips.clipBoxMeshs[5].translateZ(dis);
      this.boxClipAxisLastPos.x1 = value;
    } else if (value < this.boxClipAxisLastPos.x1) {
      let dis = (this.boxClipAxisLastPos.x1 - value) * this.lengthZ;
      this.clips.clipBoxMeshs[5].translateZ(-dis);
      this.boxClipAxisLastPos.x1 = value;
    }
    this.xChanged = true;
    this.updateSelf();
  }

  /**
   * 修改盒子剖切的X轴方向的位移（正轴）  外部调用   弃用
   * @param {0到1的值} value
   */
  changeBoxValueXPositive(value) {
    if (this.clips.axis != "BOX") return;
    value = Math.round(value * 10000) / 10000; //为了避免传值过程中的微小误差进行这步操作
    if (value < 0 || value > 1 || this.boxClipAxisLastPos.x2 == value) return;
    if (value > this.boxClipAxisLastPos.x2) {
      let dis = (value - this.boxClipAxisLastPos.x2) * this.lengthZ;
      this.clips.clipBoxMeshs[4].translateZ(dis);
      this.boxClipAxisLastPos.x2 = value;
    } else if (value < this.boxClipAxisLastPos.x2) {
      let dis = (this.boxClipAxisLastPos.x2 - value) * this.lengthZ;
      this.clips.clipBoxMeshs[4].translateZ(-dis);
      this.boxClipAxisLastPos.x2 = value;
    }
    this.xChanged = true;
    this.updateSelf();
  }

  /**
   * 修改盒子剖切的Z轴方向的位移（负轴）  外部调用  弃用
   * @param {0到1的值} value
   */
  changeBoxValueZNegative(value) {
    if (this.clips.axis != "BOX") return;
    value = Math.round(value * 10000) / 10000; //为了避免传值过程中的微小误差进行这步操作
    if (value < 0 || value > 1 || this.boxClipAxisLastPos.z1 == value) return;
    if (value > this.boxClipAxisLastPos.z1) {
      let dis = (value - this.boxClipAxisLastPos.z1) * this.lengthX;
      this.clips.clipBoxMeshs[0].translateZ(dis);
      this.boxClipAxisLastPos.z1 = value;
    } else if (value < this.boxClipAxisLastPos.z1) {
      let dis = (this.boxClipAxisLastPos.z1 - value) * this.lengthX;
      this.clips.clipBoxMeshs[0].translateZ(-dis);
      this.boxClipAxisLastPos.z1 = value;
    }
    this.zChanged = true;
    this.updateSelf();
  }

  /**
   * 修改盒子剖切的Z轴方向的位移（正轴）  外部调用  弃用
   * @param {0到1的值} value
   */
  changeBoxValueZPositive(value) {
    if (this.clips.axis != "BOX") return;
    value = Math.round(value * 10000) / 10000; //为了避免传值过程中的微小误差进行这步操作
    if (value < 0 || value > 1 || this.boxClipAxisLastPos.z2 == value) return;
    if (value > this.boxClipAxisLastPos.z2) {
      let dis = (value - this.boxClipAxisLastPos.z2) * this.lengthX;
      this.clips.clipBoxMeshs[1].translateZ(dis);
      this.boxClipAxisLastPos.z2 = value;
    } else if (value < this.boxClipAxisLastPos.z2) {
      let dis = (this.boxClipAxisLastPos.z2 - value) * this.lengthX;
      this.clips.clipBoxMeshs[1].translateZ(-dis);
      this.boxClipAxisLastPos.z2 = value;
    }
    this.zChanged = true;
    this.updateSelf();
  }

  /**
   * 修改盒子剖切的Y轴方向的位移（负轴）  外部调用  弃用
   * @param {0到1的值} value
   */
  changeBoxValueYNegative(value) {
    if (this.clips.axis != "BOX") return;
    value = Math.round(value * 10000) / 10000; //为了避免传值过程中的微小误差进行这步操作
    if (value < 0 || value > 1 || this.boxClipAxisLastPos.y1 == value) return;
    if (value > this.boxClipAxisLastPos.y1) {
      let dis = (value - this.boxClipAxisLastPos.y1) * this.lengthY;
      this.clips.clipBoxMeshs[2].translateZ(dis);
      this.boxClipAxisLastPos.y1 = value;
    } else if (value < this.boxClipAxisLastPos.y1) {
      let dis = (this.boxClipAxisLastPos.y1 - value) * this.lengthY;
      this.clips.clipBoxMeshs[2].translateZ(-dis);
      this.boxClipAxisLastPos.y1 = value;
    }
    this.yChanged = true;
    this.updateSelf();
  }

  /**
   * 修改盒子剖切的Y轴方向的位移（正轴）  外部调用  弃用
   * @param {0到1的值} value
   */
  changeBoxValueYPositive(value) {
    if (this.clips.axis != "BOX") return;
    value = Math.round(value * 10000) / 10000; //为了避免传值过程中的微小误差进行这步操作
    if (value < 0 || value > 1 || this.boxClipAxisLastPos.y2 == value) return;
    if (value > this.boxClipAxisLastPos.y2) {
      let dis = (value - this.boxClipAxisLastPos.y2) * this.lengthY;
      this.clips.clipBoxMeshs[3].translateZ(dis);
      this.boxClipAxisLastPos.y2 = value;
    } else if (value < this.boxClipAxisLastPos.y2) {
      let dis = (this.boxClipAxisLastPos.y2 - value) * this.lengthY;
      this.clips.clipBoxMeshs[3].translateZ(-dis);
      this.boxClipAxisLastPos.y2 = value;
    }
    this.yChanged = true;
    this.updateSelf();
  }

  /**
   * 退出剖切  外部调用
   */
  exitClip() {
    if (this.clips.axis) {
      // 当前项目不需要
      //   this.transformControls.detach();
      //   this.transformControls.showX = true;
      //   this.transformControls.showY = true;
      //   this.transformControls.showZ = true;
      switch (this.clips.axis) {
        case "X":
          if (this.clips.clipXMesh.parent)
            this.clips.clipXMesh.parent.remove(this.clips.clipXMesh);
          break;
        case "Y":
          if (this.clips.clipYMesh.parent)
            this.clips.clipYMesh.parent.remove(this.clips.clipYMesh);
          break;
        case "Z":
          if (this.clips.clipZMesh.parent)
            this.clips.clipZMesh.parent.remove(this.clips.clipZMesh);
          break;
        case "FREE":
          for (var i = 0; i < this.clips.clipFreeMesh.length; i++) {
            if (this.clips.clipFreeMesh[i].parent)
              this.clips.clipFreeMesh[i].parent.remove(
                this.clips.clipFreeMesh[i]
              );
          }
          break;
        case "BOX":
          // for(var i=0;i<this.clips.clipBoxMeshs.length;i++){
          //     if(this.clips.clipBoxMeshs[i].parent)
          //         this.clips.clipBoxMeshs[i].parent.remove(this.clips.clipBoxMeshs[i]);
          // }
          this.boxGroup.parent.remove(this.boxGroup);
          this.scene.remove(this.transformcontrol_box);
          break;
      }
      this.clips.axis = null;
      if (this.clipMode == "global") {
        this.renderer.clippingPlanes = [];
      } else {
        for (var i = 0; i < this.clips.materials.length; i++) {
          this.clips.materials[i].clippingPlanes = [];
        }
      }
    }
  }

  /**
   * 释放所有剖切板资源
   */
  dispose() {
    this.isInit = false;
    this.exitClip();
    if (this.clips.clipXMesh) {
      this.clips.clipXMesh.material.dispose();
      this.clips.clipXMesh.geometry.dispose();
    }
    if (this.clips.clipYMesh) {
      this.clips.clipYMesh.material.dispose();
      this.clips.clipYMesh.geometry.dispose();
    }
    if (this.clips.clipZMesh) {
      this.clips.clipZMesh.material.dispose();
      this.clips.clipZMesh.geometry.dispose();
    }
    for (var i = 0; i < this.clips.clipFreeMesh.length; i++) {
      this.clips.clipFreeMesh[i].material.dispose();
      this.clips.clipFreeMesh[i].geometry.dispose();
    }

    for (var i = 0; i < this.clips.clipBoxMeshs.length; i++) {
      this.clips.clipBoxMeshs[i].material.dispose();
      this.clips.clipBoxMeshs[i].geometry.dispose();
    }

    this.clips = {
      axis: null,
      materials: [], //保存需要剖切的材质
      //X轴向剖切
      clipXPlane: null,
      clipXMesh: null,
      //Y轴向剖切
      clipYPlane: null,
      clipYMesh: null,
      //Z轴向剖切
      clipZPlane: null,
      clipZMesh: null,
      //自由剖切
      clipFreePlane: [],
      clipFreeMesh: [],
      //盒子剖切
      clipBoxPlanes: [],
      clipBoxMeshs: [],
    };

    this.objBox = null;
  }

  //-----------------------------------------------------------------------

  /**
   * 初始化剖切板，包括轴向的和剖切盒子 ,内部调用
   */
  initPlane(type = "+X") {
    //this.isInit = true;
    this.initStat();
    this.objBox = new Box3();
    for (var i = 0; i < this.allClippingObjects.length; i++) {
      this.objBox.expandByObject(this.allClippingObjects[i]);
    }
    if (!this.isSetLength) {
      let size = this.objBox.getSize(new Vector3());
      this.lengthX = size.x * 1.5;
      this.lengthY = size.y * 1.5;
      this.lengthZ = size.z * 1.5;
    }
    this.center = this.objBox.getCenter(new Vector3());
    this.boxGroup = new Group();
    this.boxGroup.position.copy(this.objBox.min.clone().multiplyScalar(1.5));
    //X
    if (type == "+X" || type == "-X") {
      let geo = new PlaneGeometry(1, 1);
      let mat = new MeshBasicMaterial({
        color: this.clippingColor,
        transparent: true,
        opacity: this.clippingOpacity,
        side: DoubleSide,
      });
      let mesh = new Mesh(geo, mat);
      mesh.renderOrder = 999;
      mesh.scale.set(this.lengthZ, this.lengthY, 1);
      if (type == "+X") mesh.lookAt(new Vector3(-1, 0, 0)); //朝向-x轴
      else mesh.lookAt(new Vector3(1, 0, 0)); //朝向x轴
      mesh.position.add(this.center);
      //mesh.translateZ(this.lengthX/2);
      mesh._srcPos = mesh.position.clone();
      mesh._type = "clipping";
      let p = new Plane(new Vector3(0, 1, 0), 1);
      p.setFromNormalAndCoplanarPoint(
        mesh.getWorldDirection(new Vector3()),
        mesh.position
      );

      this.clips.clipXPlane = p;
      this.clips.clipXMesh = mesh;
    }
    //Y
    else if (type == "+Y" || type == "-Y") {
      let geo = new PlaneGeometry(1, 1);
      let mat = new MeshBasicMaterial({
        color: this.clippingColor,
        transparent: true,
        opacity: this.clippingOpacity,
        side: DoubleSide,
      });
      let mesh = new Mesh(geo, mat);
      mesh.renderOrder = 999;
      mesh.scale.set(this.lengthX, this.lengthZ, 1);
      if (type == "+Y") mesh.lookAt(new Vector3(0, -1, 0)); //朝向-y轴
      else mesh.lookAt(new Vector3(0, 1, 0)); //朝向+y轴
      mesh.position.add(this.center);
      //mesh.translateZ(this.lengthY/2);
      mesh._type = "clipping";
      mesh._srcPos = mesh.position.clone();
      let p = new Plane(new Vector3(0, 1, 0), 1);
      p.setFromNormalAndCoplanarPoint(
        mesh.getWorldDirection(new Vector3()),
        mesh.position
      );

      this.clips.clipYPlane = p;
      this.clips.clipYMesh = mesh;
    }
    //Z
    else if (type == "+Z" || type == "-Z") {
      let geo = new PlaneGeometry(1, 1);
      let mat = new MeshBasicMaterial({
        color: this.clippingColor,
        transparent: true,
        opacity: this.clippingOpacity,
        side: DoubleSide,
      });
      let mesh = new Mesh(geo, mat);
      mesh.renderOrder = 999;
      mesh.scale.set(this.lengthX, this.lengthY, 1);
      if (type == "+Z") mesh.lookAt(new Vector3(0, 0, -1)); //朝向-Z轴
      else mesh.lookAt(new Vector3(0, 0, 1)); //朝向Z轴
      mesh.position.add(this.center);
      //mesh.translateZ(this.lengthZ/2);
      mesh._type = "clipping";
      mesh._srcPos = mesh.position.clone();
      let p = new Plane(new Vector3(0, 1, 0), 1);
      p.setFromNormalAndCoplanarPoint(
        mesh.getWorldDirection(new Vector3()),
        mesh.position
      );

      this.clips.clipZPlane = p;
      this.clips.clipZMesh = mesh;
    }
    //自由剖切
    else if (type == "FREE") {
      let geo = new PlaneGeometry(1, 1);
      let mat = new MeshBasicMaterial({
        color: this.clippingColor,
        transparent: true,
        opacity: this.clippingOpacity,
        side: DoubleSide,
      });
      let mesh = new Mesh(geo, mat);
      mesh.renderOrder = 999;
      let length = Math.max(this.lengthX, Math.max(this.lengthY, this.lengthZ));
      mesh.scale.set(length, length, length);
      mesh.lookAt(new Vector3(0, 0, -1)); //朝向-Z轴
      mesh.position.add(this.center);
      //mesh.translateZ(length/2);
      mesh._srcPos = mesh.position.clone();
      mesh._type = "clipping";
      mesh._srcEuler = mesh.quaternion.clone();
      let p = new Plane(new Vector3(0, 1, 0), 1);
      p.setFromNormalAndCoplanarPoint(
        mesh.getWorldDirection(new Vector3()),
        mesh.position
      );

      this.clips.clipFreePlane[0] = p;
      this.clips.clipFreeMesh[0] = mesh;
    }
    //box
    else if (type == "BOX") {
      //Z
      {
        let geo = new PlaneGeometry(1, 1);
        let mat = new MeshBasicMaterial({
          color: this.clippingColor,
          transparent: true,
          opacity: this.clippingOpacity,
          side: DoubleSide,
        });

        let mesh1 = new Mesh(geo, mat);
        mesh1.renderOrder = 999;
        mesh1.scale.set(this.lengthZ, this.lengthY, 1);
        mesh1.lookAt(new Vector3(1, 0, 0));
        mesh1.translateZ(-this.lengthX / 2);
        mesh1._type = "clipping";
        mesh1._axis = "Z";
        mesh1.position.add(this.center);
        mesh1._srcPos = mesh1.position.clone();
        let p1 = new Plane(new Vector3(0, 1, 0), 1);
        p1.setFromNormalAndCoplanarPoint(
          mesh1.getWorldDirection(new Vector3()),
          mesh1.position
        );
        this.clips.clipBoxPlanes.push(p1);
        this.clips.clipBoxMeshs.push(mesh1);

        this.boxGroup.attach(mesh1);

        let mesh2 = new Mesh(geo, mat);
        mesh2.renderOrder = 998;
        mesh2.scale.set(this.lengthZ, this.lengthY, 1);
        mesh2.lookAt(new Vector3(-1, 0, 0));
        mesh2.translateZ(-this.lengthX / 2);
        mesh2._type = "clipping";
        mesh2._axis = "Z";
        mesh2.position.add(this.center);
        mesh2._srcPos = mesh2.position.clone();
        let p2 = new Plane(new Vector3(0, 1, 0), 1);
        p2.setFromNormalAndCoplanarPoint(
          mesh2.getWorldDirection(new Vector3()),
          mesh2.position
        );
        this.clips.clipBoxPlanes.push(p2);
        this.clips.clipBoxMeshs.push(mesh2);

        this.boxGroup.attach(mesh2);
      }
      //Y
      {
        let geo = new PlaneGeometry(1, 1);
        let mat = new MeshBasicMaterial({
          color: this.clippingColor,
          transparent: true,
          opacity: this.clippingOpacity,
          side: DoubleSide,
        });

        let mesh1 = new Mesh(geo, mat);
        mesh1.renderOrder = 997;
        mesh1.scale.set(this.lengthX, this.lengthZ, 1);
        mesh1.lookAt(new Vector3(0, 1, 0));
        mesh1.translateZ(-this.lengthY / 2);
        mesh1._type = "clipping";
        mesh1._axis = "Y";
        mesh1.position.add(this.center);
        mesh1._srcPos = mesh1.position.clone();
        let p1 = new Plane(new Vector3(0, 1, 0), 1);
        p1.setFromNormalAndCoplanarPoint(
          mesh1.getWorldDirection(new Vector3()),
          mesh1.position
        );
        this.clips.clipBoxPlanes.push(p1);
        this.clips.clipBoxMeshs.push(mesh1);

        this.boxGroup.attach(mesh1);

        let mesh2 = new Mesh(geo, mat);
        mesh2.renderOrder = 996;
        mesh2.scale.set(this.lengthX, this.lengthZ, 1);
        mesh2.lookAt(new Vector3(0, -1, 0));
        mesh2.translateZ(-this.lengthY / 2);
        mesh2._type = "clipping";
        mesh2._axis = "Y";
        mesh2.position.add(this.center);
        mesh2._srcPos = mesh2.position.clone();
        let p2 = new Plane(new Vector3(0, 1, 0), 1);
        p2.setFromNormalAndCoplanarPoint(
          mesh2.getWorldDirection(new Vector3()),
          mesh2.position
        );
        this.clips.clipBoxPlanes.push(p2);
        this.clips.clipBoxMeshs.push(mesh2);

        this.boxGroup.attach(mesh2);
      }
      //X
      {
        let geo = new PlaneGeometry(1, 1);
        let mat = new MeshBasicMaterial({
          color: this.clippingColor,
          transparent: true,
          opacity: this.clippingOpacity,
          side: DoubleSide,
        });

        let mesh1 = new Mesh(geo, mat);
        mesh1.renderOrder = 995;
        mesh1.scale.set(this.lengthX, this.lengthY, 1);
        mesh1.lookAt(new Vector3(0, 0, 1));
        mesh1.translateZ(-this.lengthZ / 2);
        mesh1._type = "clipping";
        mesh1._axis = "X";
        mesh1.position.add(this.center);
        mesh1._srcPos = mesh1.position.clone();
        let p1 = new Plane(new Vector3(0, 1, 0), 1);
        p1.setFromNormalAndCoplanarPoint(
          mesh1.getWorldDirection(new Vector3()),
          mesh1.position
        );
        this.clips.clipBoxPlanes.push(p1);
        this.clips.clipBoxMeshs.push(mesh1);

        this.boxGroup.attach(mesh1);

        let mesh2 = new Mesh(geo, mat);
        mesh2.renderOrder = 994;
        mesh2.scale.set(this.lengthX, this.lengthY, 1);
        mesh2.lookAt(new Vector3(0, 0, -1));
        mesh2._type = "clipping";
        mesh2._axis = "X";
        mesh2.translateZ(-this.lengthZ / 2);
        mesh2.position.add(this.center);
        mesh2._srcPos = mesh2.position.clone();
        let p2 = new Plane(new Vector3(0, 1, 0), 1);
        p2.setFromNormalAndCoplanarPoint(
          mesh2.getWorldDirection(new Vector3()),
          mesh2.position
        );
        this.clips.clipBoxPlanes.push(p2);
        this.clips.clipBoxMeshs.push(mesh2);

        this.boxGroup.attach(mesh2);
      }
    }
  }

  /**
   * 更新剖切板 , 内部调用
   */
  updateSelf() {
    if (!this.clips.axis) return;
    switch (this.clips.axis) {
      case "X":
        this.clips.clipXPlane.setFromNormalAndCoplanarPoint(
          this.clips.clipXMesh.getWorldDirection(new Vector3()),
          this.clips.clipXMesh.position
        );
        break;
      case "Y":
        this.clips.clipYPlane.setFromNormalAndCoplanarPoint(
          this.clips.clipYMesh.getWorldDirection(new Vector3()),
          this.clips.clipYMesh.position
        );
        break;
      case "Z":
        this.clips.clipZPlane.setFromNormalAndCoplanarPoint(
          this.clips.clipZMesh.getWorldDirection(new Vector3()),
          this.clips.clipZMesh.position
        );
        break;
      case "FREE":
        for (var i = 0; i < this.clips.clipFreeMesh.length; i++) {
          this.clips.clipFreePlane[i].setFromNormalAndCoplanarPoint(
            this.clips.clipFreeMesh[i].getWorldDirection(new Vector3()),
            this.clips.clipFreeMesh[i].position
          );
        }
        break;
      case "BOX":
        //以下代码用于控制移动某一个面后 6个面还能组成封闭的盒子
        if (this.xChanged) {
          let dis = this.clips.clipBoxMeshs[4].position
            .clone()
            .distanceTo(this.clips.clipBoxMeshs[5].position);
          if (dis == 0) dis = 0.00001; //不可少，当这个值为0时报警告
          this.clips.clipBoxMeshs[0].scale.x = dis;
          this.clips.clipBoxMeshs[1].scale.x = dis;
          this.clips.clipBoxMeshs[2].scale.y = dis;
          this.clips.clipBoxMeshs[3].scale.y = dis;

          let center =
            (this.clips.clipBoxMeshs[4].position.z +
              this.clips.clipBoxMeshs[5].position.z) /
            2;
          this.clips.clipBoxMeshs[0].position.z = center;
          this.clips.clipBoxMeshs[1].position.z = center;
          this.clips.clipBoxMeshs[2].position.z = center;
          this.clips.clipBoxMeshs[3].position.z = center;
          this.xChanged = false;
        }
        if (this.yChanged) {
          let dis = this.clips.clipBoxMeshs[2].position
            .clone()
            .distanceTo(this.clips.clipBoxMeshs[3].position);
          if (dis == 0) dis = 0.00001;
          this.clips.clipBoxMeshs[0].scale.y = dis;
          this.clips.clipBoxMeshs[1].scale.y = dis;
          this.clips.clipBoxMeshs[4].scale.y = dis;
          this.clips.clipBoxMeshs[5].scale.y = dis;

          let center =
            (this.clips.clipBoxMeshs[2].position.y +
              this.clips.clipBoxMeshs[3].position.y) /
            2;
          this.clips.clipBoxMeshs[0].position.y = center;
          this.clips.clipBoxMeshs[1].position.y = center;
          this.clips.clipBoxMeshs[4].position.y = center;
          this.clips.clipBoxMeshs[5].position.y = center;
          this.yChanged = false;
        }
        if (this.zChanged) {
          let dis = this.clips.clipBoxMeshs[0].position
            .clone()
            .distanceTo(this.clips.clipBoxMeshs[1].position);
          if (dis == 0) dis = 0.00001;
          this.clips.clipBoxMeshs[2].scale.x = dis;
          this.clips.clipBoxMeshs[3].scale.x = dis;
          this.clips.clipBoxMeshs[4].scale.x = dis;
          this.clips.clipBoxMeshs[5].scale.x = dis;

          let center =
            (this.clips.clipBoxMeshs[0].position.x +
              this.clips.clipBoxMeshs[1].position.x) /
            2;
          this.clips.clipBoxMeshs[2].position.x = center;
          this.clips.clipBoxMeshs[3].position.x = center;
          this.clips.clipBoxMeshs[4].position.x = center;
          this.clips.clipBoxMeshs[5].position.x = center;
          this.zChanged = false;
        }

        for (var i = 0; i < this.clips.clipBoxMeshs.length; i++) {
          this.clips.clipBoxPlanes[i].setFromNormalAndCoplanarPoint(
            this.clips.clipBoxMeshs[i].getWorldDirection(new Vector3()),
            this.clips.clipBoxMeshs[i].getWorldPosition(new Vector3())
          );
        }

        break;
    }
  }
}

export { ClippingManager };
